import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronDown, Compass, Users, Sparkles, Search, CreditCard, PartyPopper } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { NeonCursor, CheckeredHeroBackground } from '../components/NeonCursor';
import { PillarCard, StepCard, StatBlock, FeaturedEventCard } from '../components/landing/LandingCards';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const { user } = useAuth();
  // Site is live — every section is unlocked. `adminPreview` kept for backwards-compat
  // with the admin badge logic, but `showLockedContent` is always true post-launch.
  const adminPreview = !!user?.is_admin;
  const showLockedContent = true;

  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [stats, setStats] = useState({ nightOwls: 0, events: 0, cities: 0 });
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const statsRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-events`);
        const response = await axios.get(`${API}/events`);
        setFeaturedEvents(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isStatsVisible) {
          setIsStatsVisible(true);
          animateStats();
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [isStatsVisible]);

  const animateStats = () => {
    const duration = 2000;
    const targets = { nightOwls: 85, events: 10, cities: 2 };
    const start = Date.now();

    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setStats({
        nightOwls: Math.floor(targets.nightOwls * eased),
        events: Math.floor(targets.events * eased),
        cities: Math.floor(targets.cities * eased),
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    animate();
  };

  // Mouse-based parallax
  const getParallaxStyle = (intensity = 1) => {
    const x = (mousePosition.x - window.innerWidth / 2) * 0.01 * intensity;
    const y = (mousePosition.y - window.innerHeight / 2) * 0.01 * intensity;
    return { transform: `translate(${x}px, ${y}px)` };
  };

  // Scroll-based parallax
  const getScrollParallax = (speed = 0.5) => {
    return { transform: `translateY(${scrollY * speed}px)` };
  };

  // Combined parallax for hero elements
  const getHeroParallax = (scrollSpeed = 0.3, mouseIntensity = 0.5) => {
    const mouseX = (mousePosition.x - window.innerWidth / 2) * 0.01 * mouseIntensity;
    const mouseY = (mousePosition.y - window.innerHeight / 2) * 0.01 * mouseIntensity;
    const scrollOffset = scrollY * scrollSpeed;
    return { 
      transform: `translate(${mouseX}px, ${mouseY + scrollOffset}px)`,
      opacity: Math.max(0, 1 - scrollY / 600)
    };
  };

  return (
    <div className="min-h-screen bg-[#050505] cursor-none" data-testid="landing-page">
      <Seo
        title="Underground Nightlife in the UK & India"
        description="Unight is where underground music finds its people — discover verified events, join guest lists, and buy tickets across London, Mumbai, Bangalore, Goa and beyond."
        path="/"
      />
      <NeonCursor />

      {/* Hero Section with Checkered Neon Background */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden" 
        data-testid="hero-section"
      >
        {/* Checkered Neon Background with parallax */}
        <CheckeredHeroBackground scrollY={scrollY} />

        {/* Hero Content - Faster parallax creates depth */}
        <div 
          className="relative container mx-auto px-4 md:px-8 text-center pt-20"
          style={getHeroParallax(0.2, 0.3)}
        >
          <div className="max-w-4xl mx-auto">
            {/* Unight Logo */}
            <div 
              className="mb-8 opacity-0 animate-fade-in"
              style={{ 
                animationDelay: '0.2s', 
                animationFillMode: 'forwards',
                transform: `translateY(${scrollY * -0.1}px) scale(${Math.max(0.8, 1 - scrollY / 2000)})`
              }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
                alt="Unight"
                className="h-32 md:h-48 lg:h-56 mx-auto"
                data-testid="hero-logo"
              />
            </div>

            {/* Headline */}
            <h1 
              className="font-['Josefin_Sans'] text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-6 opacity-0 animate-hero-title"
              style={{ animationFillMode: 'forwards' }}
              data-testid="hero-title"
            >
              A New Home For<br /><span className="text-[#B5FF00] italic font-light whitespace-nowrap">Grassroot Events</span>
            </h1>

            {/* Subheading */}
            <p 
              className="text-zinc-400 text-base md:text-lg max-w-xl mx-auto mb-10 opacity-0 animate-fade-in-up"
              style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}
            >
              Discover underground events, connect with your crew, and experience nights you'll never forget. The UK & India's premier nightlife discovery platform.
            </p>

            {/* CTAs */}
            <div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up"
              style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}
            >
              <Link
                to="/discovery"
                className="btn-primary flex items-center gap-2 group cursor-none"
                data-testid="hero-cta-discovery"
              >
                MEET THE ARTISTS
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/events"
                className="btn-secondary-dark flex items-center gap-2 cursor-none"
                data-testid="hero-cta-get-tickets"
              >
                GET TICKETS
              </Link>
            </div>

            {/* Scroll Indicator */}
            <div className="flex flex-col items-center gap-2 pt-16 pb-8">
              <ChevronDown size={24} strokeWidth={1} className="text-zinc-500 animate-bounce" />
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Section - Right under hero */}
      <div className="border-t border-b border-zinc-800 py-3 overflow-hidden bg-[#050505]">
        <div className="marquee">
          <div className="marquee-content">
            <span className="text-[#B5FF00] font-mono text-sm tracking-widest whitespace-nowrap">
              &nbsp;&nbsp;COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA ⚡ COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA&nbsp;&nbsp;
            </span>
            <span className="text-[#B5FF00] font-mono text-sm tracking-widest whitespace-nowrap">
              &nbsp;&nbsp;COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA ⚡ COMMUNITY + PROMOTION + TICKETING ⚡ JOIN THE BETA&nbsp;&nbsp;
            </span>
          </div>
        </div>
      </div>

      {/* Why Our Platform Section */}
      <section 
        className="py-24 md:py-32 relative overflow-hidden" 
        data-testid="why-section"
      >
        {/* Background image with parallax */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-15"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/75vu016v_6.jpg')`,
              transform: `translateY(${(scrollY - 600) * 0.15}px) scale(1.1)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div 
            className="text-center mb-16"
            style={{ transform: `translateY(${Math.max(0, (scrollY - 500) * -0.05)}px)` }}
          >
            <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-4 block">WHY CHOOSE US</span>
            <h2 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white">
              THE UNDERGROUND EXPERIENCE
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <PillarCard 
              icon={<Compass size={32} strokeWidth={1.5} />}
              title="DISCOVER"
              description="Find the best parties, raves, and underground events across the UK & India"
              delay={0}
            />
            <PillarCard 
              icon={<Users size={32} strokeWidth={1.5} />}
              title="CONNECT"
              description="Meet like-minded people and join the nightlife community"
              delay={100}
            />
            <PillarCard 
              icon={<Sparkles size={32} strokeWidth={1.5} />}
              title="EXPERIENCE"
              description="Never miss out on unforgettable nights and hidden gems"
              delay={200}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section - Hidden in pre-launch (visible to admins) */}
      {showLockedContent && (
      <section className="py-24 md:py-32 bg-[#0A0A0A] border-t border-zinc-800 relative" data-testid="how-section">
        {/* Grid pattern with parallax */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(to right, #B5FF00 1px, transparent 1px),
              linear-gradient(to bottom, #B5FF00 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            transform: `translateY(${(scrollY - 1200) * 0.1}px)`,
          }}
        />

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-4 block">SIMPLE PROCESS</span>
            <h2 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white">
              HOW IT WORKS
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-[#B5FF00]/30 to-transparent" />
            
            <StepCard 
              number="01"
              icon={<Search size={24} strokeWidth={1.5} />}
              title="Browse Events"
              description="Explore curated nightlife experiences across your city"
              delay={0}
            />
            <StepCard 
              number="02"
              icon={<CreditCard size={24} strokeWidth={1.5} />}
              title="Get Tickets"
              description="Secure your spot instantly with one tap"
              delay={150}
            />
            <StepCard 
              number="03"
              icon={<PartyPopper size={24} strokeWidth={1.5} />}
              title="Show Up & Vibe"
              description="Enjoy the night — we've got you covered"
              delay={300}
            />
          </div>
        </div>
      </section>
      )}

      {/* Featured Experiences Section — admin-only while events are paused */}
      {adminPreview && (
      <section className="py-24 md:py-32 border-t border-zinc-800" data-testid="featured-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="font-mono text-xs tracking-widest text-[#B5FF00] mb-2 block">FEATURED · ADMIN PREVIEW</span>
              <h2 className="font-['Josefin_Sans'] text-4xl md:text-5xl font-bold text-white">
                UPCOMING EXPERIENCES
              </h2>
            </div>
            <Link 
              to="/events"
              className="font-mono text-sm text-zinc-500 flex items-center gap-2 hover:text-zinc-400 cursor-none transition-colors"
            >
              VIEW ALL EVENTS <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredEvents.map((event, index) => (
              <FeaturedEventCard key={event.id} event={event} index={index} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Statistics Section - Hidden in pre-launch (visible to admins) */}
      {showLockedContent && (
      <section 
        ref={statsRef}
        className="py-24 md:py-32 border-t border-zinc-800 relative overflow-hidden" 
        data-testid="stats-section"
      >
        <div 
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/2ckfylub_8.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-[#050505]/85" />

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <StatBlock 
              value={`${stats.nightOwls}+`}
              label="Night Owls"
            />
            <StatBlock 
              value={`${stats.events}+`}
              label="Events"
            />
            <StatBlock 
              value={`${stats.cities}`}
              label="Cities & Growing"
            />
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-zinc-400 text-xl md:text-2xl italic mb-6 leading-relaxed">
              "Finally, a platform that gets underground culture. No mainstream noise, just pure vibes."
            </blockquote>
            <cite className="font-mono text-sm text-[#B5FF00]">— Early Beta User</cite>
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 border-t border-zinc-800 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(181, 255, 0, 0.08) 0%, transparent 70%)',
              animation: 'pulse 4s ease-in-out infinite',
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-['Josefin_Sans'] text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              READY TO OWN<br /><span className="text-[#B5FF00] neon-text-glow">THE NIGHT?</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-8">
              Discover the best underground events in the UK & India — grab your tickets before they're gone.
            </p>

            <Link 
              to="/events"
              className="btn-primary inline-flex items-center gap-2 animate-pulse-glow cursor-none"
              data-testid="cta-get-tickets"
            >
              GET TICKETS
              <ArrowRight size={18} />
            </Link>

            <p className="text-zinc-600 text-sm font-mono mt-4">
              Guest checkout available — no sign-up required.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <span className="footer-brand">UNIGHT</span>
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_venue-pulse-14/artifacts/8f7het1z_Unight%202d%20Logos%20%28TM%29-05.png"
              alt="Unight"
              className="h-8"
            />
            <span className="font-mono text-xs text-zinc-600 tracking-widest">
              © 2025 UNIGHT. ALL RIGHTS RESERVED.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
